import { Fragment, useMemo, useState } from 'react';
import plusRound from '~/assets/icons/plus_round.svg';
import { playSound, Sound } from '~/assets/sounds';
import { FadeContainer } from '~/components/ui/FadeContainer';
import { Text } from '~/components/ui/Text';
import { rage, useObservable } from '~/rage';
import { IContact, IPhoneContactPresence } from '~/shared';
import { ContactsStages } from '../../models/Contacts';
import {
	setActiveContactsState,
	setSelectedContact,
	useLayoutDispatch,
} from '../../redux';
import { AppMessengerPlayerCard } from '../AppMessengerPlayerCard';
import { SearchInput } from '../SearchInput';
import {
	StyledContainer,
	StyledHead,
	StyledList,
	StyledNoContactsContainer,
	StyledOfflineTitle,
	StyledPlayersList,
} from './AppContactsAllContacts.styled';

const ContactItem = ({
	contact,
	online,
	onClick,
}: {
	contact: IContact;
	online: boolean;
	onClick: () => void;
}) => (
	<div onClick={onClick} onKeyDown={() => {}} role="button" tabIndex={0}>
		<AppMessengerPlayerCard
			nickname={contact.name}
			online={online}
			phoneNumber={contact.phoneNumber}
		/>
	</div>
);

const AppContactsAllContacts = () => {
	const dispatch = useLayoutDispatch();
	const [searchInputValue, setSearchInputValue] = useState('');

	const contacts = useObservable(rage.contacts.getContacts()) ?? [];
	const presenceList =
		useObservable<IPhoneContactPresence[]>(
			rage.messages.getContactsPresence(),
		) ?? [];

	const { onlineContacts, offlineContacts } = useMemo(() => {
		const online: IContact[] = [];
		const offline: IContact[] = [];
		for (const contact of contacts) {
			if (
				presenceList.some(
					(p) => p.phoneNumber === contact.phoneNumber && p.isOnline,
				)
			) {
				online.push(contact);
			} else {
				offline.push(contact);
			}
		}
		return { onlineContacts: online, offlineContacts: offline };
	}, [contacts, presenceList]);

	const filteredOnline = useMemo(() => {
		if (!searchInputValue.trim()) return onlineContacts;
		const q = searchInputValue.trim().toLowerCase();
		return onlineContacts.filter(
			(c) => c.name.toLowerCase().includes(q) || c.phoneNumber.includes(q),
		);
	}, [onlineContacts, searchInputValue]);

	const filteredOffline = useMemo(() => {
		if (!searchInputValue.trim()) return offlineContacts;
		const q = searchInputValue.trim().toLowerCase();
		return offlineContacts.filter(
			(c) => c.name.toLowerCase().includes(q) || c.phoneNumber.includes(q),
		);
	}, [offlineContacts, searchInputValue]);

	const handleContactClick = (contact: IContact) => {
		dispatch(setSelectedContact(contact));
		dispatch(setActiveContactsState(ContactsStages.INFO_CONTACT));
	};

	return (
		<div>
			<StyledHead>
				<div />
				<Text>Мои контакты</Text>
				<img
					src={plusRound}
					onClick={() => {
						dispatch(setActiveContactsState(ContactsStages.ADD_CONTACT));
						playSound(Sound.ClickButton);
					}}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				/>
			</StyledHead>
			<StyledContainer>
				<SearchInput
					value={searchInputValue}
					onChange={setSearchInputValue}
					placeholder="Введите имя для поиска"
				/>
				{filteredOffline.length === 0 && filteredOnline.length === 0 ? (
					<StyledNoContactsContainer>
						<Text>У вас нет контактов</Text>
					</StyledNoContactsContainer>
				) : (
					<FadeContainer
						height={365}
						style={{ marginTop: 20, paddingBottom: 80 }}
					>
						<StyledPlayersList>
							<StyledList>
								{filteredOnline.map((contact) => (
									<ContactItem
										key={contact.phoneNumber}
										contact={contact}
										online
										onClick={() => handleContactClick(contact)}
									/>
								))}
							</StyledList>
							{filteredOffline.length > 0 && (
								<Fragment>
									<StyledOfflineTitle>
										<div className="dot" />
										<Text>Не в сети</Text>
									</StyledOfflineTitle>
									<StyledList>
										{filteredOffline.map((contact) => (
											<ContactItem
												key={contact.phoneNumber}
												contact={contact}
												online={false}
												onClick={() => handleContactClick(contact)}
											/>
										))}
									</StyledList>
								</Fragment>
							)}
						</StyledPlayersList>
					</FadeContainer>
				)}
			</StyledContainer>
		</div>
	);
};

export { AppContactsAllContacts };
